import invariant from 'invariant';
import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';
import { styled } from 'styled-components';

import type { ApiAddress } from 'api/types/ApiAddress';
import type { ApiRemoteOptions } from 'api/types/ApiRemoteOptions';
import { Box } from 'components/Box/Box';
import { Divider } from 'components/Divider/Divider';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { H2 } from 'components/Text/H2';
import { useI18n } from 'i18n/hooks/useI18n';
import { ListingLocationLabel } from 'modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from 'modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { spacing } from 'theme/theme';
import { getCityStateString } from 'utils/address/getCityStateString';
import { getCountryName, getUsStateName } from 'utils/internationalization';

const TagRowContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: row wrap;
  gap: 8px;

  &:empty {
    display: none;
  }
`;

type Props = {
  address: ApiAddress;
  listingName: string;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  orgName: string | null;
  orgUrl: string | null;
  remoteOptions: ApiRemoteOptions;
};

export function ListingViewSidebarTitle({
  address,
  listingName,
  listingType,
  orgName,
  orgUrl,
  remoteOptions,
}: Props) {
  const { i18n } = useI18n();

  const workLocation = useMemo(() => {
    if (remoteOptions.remoteZone === 'WORLD') {
      return i18n('Anywhere in the World');
    }

    if (remoteOptions.remoteZone === 'COUNTRY') {
      invariant(remoteOptions.remoteCountry, 'Undefined remoteCountry');
      return sprintf(getText('Anywhere in %(location)s'), {
        location: getCountryName(remoteOptions.remoteCountry),
      });
    }

    if (remoteOptions.remoteZone === 'STATE') {
      invariant(remoteOptions.remoteState, 'Undefined remoteState');
      return getUsStateName(remoteOptions.remoteState);
    }

    if (
      remoteOptions.locationType === 'ONSITE' ||
      remoteOptions.remoteZone === 'CITY'
    ) {
      return getCityStateString(address);
    }
  }, [
    i18n,
    address,
    remoteOptions.locationType,
    remoteOptions.remoteCountry,
    remoteOptions.remoteState,
    remoteOptions.remoteZone,
  ]);

  return (
    <div aria-hidden="true">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={spacing[16]}
      >
        <H2 styleAs="h3" mb="0">
          {listingName}
        </H2>

        {orgName && orgUrl ? (
          <Box mb={spacing[16]}>
            <StyledNativeLinkWithRef href={orgUrl} data-qa-id="org-link">
              {orgName}
            </StyledNativeLinkWithRef>
          </Box>
        ) : null}

        {orgName && !orgUrl ? <Box mb={spacing[16]}>{orgName}</Box> : null}

        <TagRowContainer>
          <ListingLocationTypeLabel
            listingType={listingType}
            locationType={remoteOptions.locationType}
          />

          {workLocation &&
          (listingType !== 'EVENT' ||
            (listingType === 'EVENT' &&
              remoteOptions.locationType !== 'REMOTE')) ? (
            <ListingLocationLabel
              location={workLocation}
              locationType={remoteOptions.locationType}
            />
          ) : null}
        </TagRowContainer>
      </Box>
      <Divider marginTop={spacing[24]} marginBottom={spacing[24]} />
    </div>
  );
}
